import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Card } from "@mantine/core";
import { DateTime } from 'luxon';
import React, { useEffect, useState } from "react";
import ShiftSessionsApi from "../../../data/api/shiftSessionsApi";
import { IPayEvent } from "../../../types/aba";
import { IAssociation } from '../../../types/association/Association';
import { IShiftSession } from "../../../types/shifts/Shift";
import { ShiftPayEventModal } from './ShiftPayEventModal';
import { ShiftSessionFilters } from './ShiftSessionFilters';
import { ShiftSessionTable } from './ShiftSessionTable';
interface ShiftPayEventCreatorProps {
  association: IAssociation;
  onPayEventCreated: (payEvent?: IPayEvent) => void;
  isVisible?: boolean;
}

interface ShiftSessionWithName extends IShiftSession {
  shiftName: string;
}

// (Name generation moved to modal; no local default name function needed.)

const getDefaultDateRange = () => {
  const today = DateTime.now();
  const startOfYear = today.startOf('year');
  return {
    startDate: startOfYear,
    endDate: today
  };
};

const ShiftPayEventCreator: React.FunctionComponent<ShiftPayEventCreatorProps> = ({
  association,
  onPayEventCreated,
  isVisible = true,
}) => {

  const associationId = association._id;

  const [loading, setLoading] = useState(false);
  const [_shiftSessionsRaw, setShiftSessions] = useState<ShiftSessionWithName[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Date filters
  const [shiftName, _setName] = useState('');
  const [startDate, setStartDate] = useState(() => getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState(() => getDefaultDateRange().endDate);
  const [paymentStatuses, setPaymentStatuses] = useState<Array<'unpaid' | 'partial' | 'paid'>>(['unpaid', 'partial']);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<ShiftSessionWithName | null>(null);

  // Load shift sessions when component becomes visible or date filters change
  useEffect(() => {
    if (isVisible) {
      loadShiftSessions();
    }
  }, [isVisible, associationId, startDate, endDate]);

  const loadShiftSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const sessions = await ShiftSessionsApi.getShiftSessionsForPayment(
        associationId,
        startDate.toISODate(),
        endDate.toISODate()
      );
      setShiftSessions(sessions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shift sessions');
    } finally {
      setLoading(false);
    }
  };



  const handleViewAppointments = (session: ShiftSessionWithName) => {
    setSelectedSession(session);
    setShowModal(true);
  };

  const handlePayEventCreated = (payEvent?: IPayEvent) => {
    // Update the session status in local state if we have a selected session
    if (selectedSession) {
      setShiftSessions(prev => prev.map(session =>
        session._id === selectedSession._id
          ? { ...session, paymentStatus: 'paid' as const }
          : session
      ));
    }

    setShowModal(false);
    setSelectedSession(null);
    onPayEventCreated(payEvent);
  };

  const handleSessionUpdated = (updatedSession: ShiftSessionWithName) => {
    // Update the session in our local state
    setShiftSessions(prev => prev.map(session =>
      session._id === updatedSession._id ? updatedSession : session
    ));

    // Also update the selected session if it's the same one
    if (selectedSession && selectedSession._id === updatedSession._id) {
      setSelectedSession(updatedSession);
    }
  };

  const handlePaymentStatusUpdate = async (session: ShiftSessionWithName, newStatus: 'unpaid' | 'partial' | 'paid') => {
    setError(null);
    try {

      const updatedSession = await ShiftSessionsApi.updatePaymentStatus(session._id, newStatus);

      // Update the session with the new payment status
      const updatedSessionWithName: ShiftSessionWithName = {
        ...updatedSession,
        shiftName: session.shiftName
      };

      handleSessionUpdated(updatedSessionWithName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update payment status');
    }
  };

  const handleApplyFilters = () => {
    loadShiftSessions();
  };

  const handleResetFilters = () => {
    const defaultDates = getDefaultDateRange();
    setStartDate(defaultDates.startDate);
    setEndDate(defaultDates.endDate);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSession(null);
  };

  const shiftSessionsByName = shiftName?.length > 0
    ? _shiftSessionsRaw.filter(session => session.shiftName.includes(shiftName))
    : _shiftSessionsRaw;
  const shiftSessions = shiftSessionsByName.filter(s => paymentStatuses.includes((s.paymentStatus ?? 'unpaid')));

  return (
    <Card>
      <Card.Section withBorder p="md">
        <h5 className="mb-0">
          <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
          Create Pay Event from Shift Sessions
        </h5>
      </Card.Section>

      <div style={{ padding: "1rem" }}>
        {error && (
          <Alert color="red" className="mb-3">
            <strong>Error:</strong> {error}
          </Alert>
        )}

        <Alert color="blue" className="mb-4">
          <h6>Shift Session Payments</h6>
          <p className="mb-0">
            Select a shift session to view its appointments and create a pay event for payment processing.
            Use the filters above to include Unpaid, Partial, or Paid sessions.
          </p>
        </Alert>

        <ShiftSessionFilters
          name={shiftName}
          startDate={startDate}
          endDate={endDate}
          onNameChange={_setName}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          isLoading={loading}
          paymentStatuses={paymentStatuses}
          onPaymentStatusesChange={setPaymentStatuses}
        />

        <ShiftSessionTable
          sessions={shiftSessions}
          onViewAppointments={handleViewAppointments}
          onPaymentStatusUpdate={handlePaymentStatusUpdate}
          isLoading={loading}
        />
      </div>

      <ShiftPayEventModal
        opened={showModal}
        onClose={handleCloseModal}
        session={selectedSession}
        associationId={associationId}
        onPayEventCreated={handlePayEventCreated}
        onSessionUpdated={handleSessionUpdated}
      />
    </Card>
  );
};

export default ShiftPayEventCreator;
